import {useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback} from 'react';
import {
    Alert,
    Box,
    Skeleton,
    Button,
    Stack,
    CircularProgress,
    Backdrop,
    Typography,
    Popover,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FunctionsIcon from '@mui/icons-material/Functions';
import {SplitTabsPanel, SnackbarField, useSplitTabsHeaderCompact} from '@/components/common';
import {BalanceSheetOutlinedIcon, ProfitLossOutlinedIcon, CashFlowOutlinedIcon} from '@/assets/icons/FsStatementIcon';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import {
    post as postAdjustment,
} from '@/services/auditService/auditAdjustmentService';
import BankLoan from '@/adjustment/adjust/dialogs/loan/BankLoan';
import VehicleLoan from '@/adjustment/adjust/dialogs/loan/VehicleLoan';
import {
    getAdjustmentWithWorkingPapers,
    saveWithWorkingPapers,
    type AuditAdjustHeader,
    type AuditAdjustLine
} from '@/services/auditService/auditAdjustOrchestrate';
import {useAdjustmentStore, useFsCategoryStore, useAdjustDraftStore, useFsAdjustStore} from '@/store/audit';
import type {AuditedMode} from '@/pages/components/fs-report/BalanceSheet';
import AdjustDialogLauncher from '@/adjustment/adjust/support/DialogLauncher';
import AdjustTabsDialog from '@/adjustment/adjust/support/AdjustTabsDialog';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import userStore from '@/store/admin/userStore';
import {BalanceSheet, ProfitLoss, Cashflow} from "@/pages/components/fs-report";
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {useQueryAdjustmentList, useQueryRoundingAdjust} from '@/services/auditService/adjustmentQueries';
import {useQueryClient} from '@tanstack/react-query';
import {FsPreviewDownloadDialog} from '@/pages/components/audit/adjustment/preview/FsPreviewDownloadDialog';
import {
    requestAuditedRoundingGate,
    requestTaxRoundingGate,
    subscribeRoundingSaved,
} from '@/pages/components/fs-report/roundingAdjustHelpers';
import {changeStatus as changeFsStatus} from '@/services/auditService/auditFsAnnualService';
import {AnnualFsStatusEnum, AuditCompanyFSCategoryService} from '@/services/auditService';
import {
    clearFsAdjustmentCachesForAnnual,
    clearFsAdjustmentRefreshMark,
    FS_ADJUSTMENT_SILENT_REFRESH_EVENT,
    FS_ADJUSTMENT_STALE_EVENT,
    readFsAdjustmentRefreshMark,
    scheduleCoalescedAdjustmentListRefresh,
    type FsAdjustmentSilentRefreshDetail,
    type FsAdjustmentStaleDetail,
} from '@/utils/fsAdjustmentRefresh';
import type {AuditPeriodSlice, WpPeriodScope} from '@/utils/auditPeriodSplit';
import {
    resolveWpPeriodTabMode,
    sliceToPeriodScope,
} from '@/utils/auditPeriodWpMulti';
import {WpSnackbarContext} from '@/adjustment/hooks/WpSnackbarContext';
import type {ShowSnackbarFn} from '@/adjustment/adjust/dialogs/utils/working-helpers';
import {
    type PlMultiViewMode,
} from '@/pages/components/fs-report/plMultiTotalView';
import AuditProgramPanel from '@/pages/components/audit-program/AuditProgramPanel';
import IssuesOutstandingPanel from '@/pages/components/audit/adjustment/IssuesOutstandingPanel';
import {exportFsWpIssuesExcel} from '@/services/auditService/auditFsWpIssueService';
interface FsAdjustmentProps {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    yearOfAssessment?: string | null;
    inCorpDate?: string; // 成立时间，格式：dd/MM/yyyy
    onBack?: () => void;
    /** 返回列表页时触发，用于父页面重新拉取列表数据 */
    onRequestListRefresh?: () => void;
    onAfterModifySuccess?: () => void;
    workflow?: 'audit' | 'account'; // 标识来源工作流，默认 'audit'
}

/** headerLeft: shrink mode hides period Tabs; Multiple/Total toggle stays. Must render under SplitTabsPanel. */
function FsPlPeriodHeaderLeft(props: {
    plMultiViewMode: PlMultiViewMode;
    onToggleMultiViewMode: () => void;
    plPeriodKey: string;
    onPeriodChange: (key: string) => void;
    plSlices: AuditPeriodSlice[];
}) {
    const headerCompact = useSplitTabsHeaderCompact();
    const {plMultiViewMode, onToggleMultiViewMode, plPeriodKey, onPeriodChange, plSlices} = props;
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{minHeight: 36}}>
            <Tooltip
                title={plMultiViewMode === 'multiple'
                    ? 'Switch to Total (sum Client across periods)'
                    : 'Switch to Multiple period tabs'}
            >
                <IconButton
                    size="small"
                    aria-label={plMultiViewMode === 'multiple' ? 'Total view' : 'Multiple view'}
                    onClick={onToggleMultiViewMode}
                    sx={{p: 0.5}}
                >
                    {plMultiViewMode === 'multiple'
                        ? <FunctionsIcon fontSize="small"/> /* TotalIcon */
                        : <ViewModuleIcon fontSize="small"/> /* MultipleIcon */}
                </IconButton>
            </Tooltip>
            {plMultiViewMode === 'multiple' ? (
                <Tabs
                    value={plPeriodKey || plSlices[0]?.key || false}
                    onChange={(_, v) => onPeriodChange(String(v))}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        maxWidth: headerCompact ? 240 : undefined,
                        '& .MuiTab-root': {minHeight: 36, py: 0, minWidth: headerCompact ? 64 : undefined},
                    }}
                >
                    {plSlices.map((s) => (
                        <Tab
                            key={s.key}
                            value={s.key}
                            label={s.yearOfAssessment || s.label}
                        />
                    ))}
                </Tabs>
            ) : null}
        </Stack>
    );
}

export default function FsAdjustment({
                                         companyId,
                                         annualId,
                                         companyName,
                                         yearOfAssessment,
                                         inCorpDate,
                                         onBack,
                                         onRequestListRefresh,
                                         onAfterModifySuccess,
                                         workflow = 'audit'
                                     }: FsAdjustmentProps) {
    const [companyIdState, setCompanyIdState] = useState<number>(companyId);
    const [annualIdState, setAnnualIdState] = useState<number>(annualId);
    const [inCorpDateState, setInCorpDateState] = useState<string | undefined>(inCorpDate);
    // 始终显示所有 chips
    const showAllSchedules = true;
    const [fsOpeningEditMode, setFsOpeningEditMode] = useState<boolean>(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
    const [isReloading, setIsReloading] = useState<boolean>(false);
    // FS_TO_FS 模式的 balance error 状态
    const [fsOpeningError, setFsOpeningError] = useState<string | null>(null);
    const [fsOpeningWarning, setFsOpeningWarning] = useState<string | null>(null);
    // FS_TO_FS 模式下所有 EXCLUSIVE_CODES 输入值都为 0
    const [allExclusiveCodesZero, setAllExclusiveCodesZero] = useState<boolean>(true);

    // 特殊组件渲染状态
    const [specialDialogType, setSpecialDialogType] = useState<'BankLoan' | 'VehicleLoan' | null>(null);
    const [specialDialogPayload, setSpecialDialogPayload] = useState<any>(null);

    const queryClient = useQueryClient();

    // 使用新的整合接口获取 FS 数据和调整数据（已包含所有 header 数据）
    const {
        data: adjustmentData,
        isLoading: adjustmentLoading,
        isFetching: adjustmentFetching,
        refetch: refetchAdjustmentList,
        error: adjustmentError
    } = useQueryAdjustmentList(
        companyIdState,
        annualIdState,
        inCorpDateState,
        !!(companyIdState && annualIdState)
    );

    const {
        data: roundingGroup,
        isFetching: isRoundingFetching,
        isPlaceholderData: isRoundingPlaceholder,
        refetch: refetchRoundingGroup,
    } = useQueryRoundingAdjust(annualIdState, !!(companyIdState && annualIdState));

    const loading = adjustmentLoading;
    const error = adjustmentError ? String(adjustmentError) : null;

    // 首屏仅依赖 queryAdjustmentList；draftFinalAmount / rounding 不阻塞 Skeleton
    const showSkeleton = loading || isReloading;

    const tabContentSkeleton = useMemo(() => {
        return (
            <Box sx={{p: 2}}>
                <Skeleton variant="rectangular" height={44} sx={{mb: 2}}/>
                {Array.from({length: 10}).map((_, idx) => (
                    <Stack key={idx} direction="row" spacing={2} sx={{mb: 1}}>
                        <Skeleton variant="text" width="45%"/>
                        <Skeleton variant="text" width="15%"/>
                        <Skeleton variant="text" width="20%"/>
                        <Skeleton variant="text" width="20%"/>
                    </Stack>
                ))}
            </Box>
        );
    }, []);

    // ✅ 监听加载完成或错误，关闭重新加载状态
    useEffect(() => {
        if (isReloading && !adjustmentLoading && !adjustmentFetching) {
            // 数据加载完成（无论成功或失败），延迟关闭 Skeleton 以确保数据已同步
            const timer = setTimeout(() => {
                setIsReloading(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isReloading, adjustmentLoading, adjustmentFetching]);

    useEffect(() => {
        const mark = readFsAdjustmentRefreshMark();
        if (!mark) return;
        if (mark.companyId !== companyIdState || mark.annualId !== annualIdState) return;

        setIsReloading(true);

        try {
            useFsAdjustStore.getState().clear();
        } catch {
        }

        queryClient.invalidateQueries({
            queryKey: ['adjustment', 'list', companyIdState, annualIdState]
        });
        queryClient.invalidateQueries({
            queryKey: ['adjustment', 'workingRecord', companyIdState, annualIdState]
        });

        refetchAdjustmentList()
            .then(() => {
                clearFsAdjustmentRefreshMark();
            })
            .catch(() => {
            });
    }, [companyIdState, annualIdState, queryClient, refetchAdjustmentList]);

    // CompanyAnnualDetail 等更新后：已挂载时按 companyId/annualId 立即刷新
    useEffect(() => {
        const onStale = (event: Event) => {
            const detail = (event as CustomEvent<FsAdjustmentStaleDetail>).detail;
            if (!detail) return;
            if (Number(detail.companyId) !== companyIdState || Number(detail.annualId) !== annualIdState) {
                return;
            }
            setIsReloading(true);
            try {
                useFsAdjustStore.getState().clear();
            } catch {
            }
            queryClient.invalidateQueries({
                queryKey: ['adjustment', 'list', companyIdState, annualIdState],
            });
            queryClient.invalidateQueries({
                queryKey: ['adjustment', 'workingRecord', companyIdState, annualIdState],
            });
            refetchAdjustmentList()
                .then(() => clearFsAdjustmentRefreshMark())
                .catch(() => {});
            refetchRoundingGroup().catch(() => {});
        };
        window.addEventListener(FS_ADJUSTMENT_STALE_EVENT, onStale);
        return () => window.removeEventListener(FS_ADJUSTMENT_STALE_EVENT, onStale);
    }, [companyIdState, annualIdState, queryClient, refetchAdjustmentList, refetchRoundingGroup]);

    // Sync internal state when props change (e.g., entering from list)
    // 注意：此 clear 在 useLayoutEffect 之后执行；必须重置 sync ref，并由后续 effect 重新灌入 React Query 缓存，
    // 否则 Back 再进入同一记录时会出现「query 有缓存但 store 为空」的空页。
    const prevAdjustmentDataRef = useRef<any>(null);
    useEffect(() => {
        setCompanyIdState(companyId);
        setAnnualIdState(annualId);
        // 清理跨公司/年度的暂存，避免污染
        try {
            useAdjustmentStore.getState().clearAll();
            useAdjustDraftStore.getState().cleanAll();
            useFsAdjustStore.getState().clear();
        } catch {
        }
        prevAdjustmentDataRef.current = null;
    }, [companyId, annualId]);

    // 进入 Adjustment 时清空 fsCategoryStore，避免 Account GL / FS Summary 会话残留污染 AL 等
    // （仅在 company/annual key 变化时 reset 不够：组件 remount 时 prev 为 null 会跳过）
    useEffect(() => {
        try {
            useFsCategoryStore.getState().reset();
        } catch {
        }
    }, [companyIdState, annualIdState]);

    /**
     * WP / orchestrator 保存成功后的无感刷新：
     * - 不清 fsAdjustStore、不设 isReloading（无 Skeleton）
     * - 合并 burst 内多次通知，queryAdjustmentList 仅 refetch 一次
     * - 新数据经既有 useLayoutEffect → syncFromQueryData → draftFinalAmount 管道灌入
     */
    const silentRefreshAfterWpSave = useCallback(async () => {
        if (!companyIdState || !annualIdState) return;
        draftFinalAmountKeyRef.current = null;
        // WHY: list drives BS/PL paint; rounding + issue can run in parallel (same end state, less wall time)
        const listRefresh = scheduleCoalescedAdjustmentListRefresh(
            companyIdState,
            annualIdState,
            () => refetchAdjustmentList(),
        ).catch((e) => {
            console.error('[FsAdjustment] Silent adjustment list refresh failed:', e);
        });
        const roundingRefresh = refetchRoundingGroup().catch((e) => {
            console.error('[FsAdjustment] Silent rounding refresh failed:', e);
        });
        const issueRefresh = queryClient.invalidateQueries({
            queryKey: ['audit-fs-wp-issue', companyIdState, annualIdState],
        }).catch((e) => {
            console.error('[FsAdjustment] Silent issue list refresh failed:', e);
        });
        await Promise.all([listRefresh, roundingRefresh, issueRefresh]);
    }, [companyIdState, annualIdState, refetchAdjustmentList, refetchRoundingGroup, queryClient]);

    // paper-registry WP：saveOrUpdate 成功后（弹窗不关）由服务层派发，FsAdjustment 无感更新 BS/PL
    const dataSaved = useAdjustDraftStore((state) => state.dataSaved);
    const setDataSaved = useAdjustDraftStore((state) => state.setDataSaved);

    useEffect(() => {
        if (!dataSaved) return;
        void silentRefreshAfterWpSave().finally(() => setDataSaved(false));
    }, [dataSaved, silentRefreshAfterWpSave, setDataSaved]);

    // paper-registry WP：saveOrUpdate 成功后（弹窗不关）由服务层派发，FsAdjustment 无感更新 BS/PL
    useEffect(() => {
        const onSilentRefresh = (event: Event) => {
            const detail = (event as CustomEvent<FsAdjustmentSilentRefreshDetail>).detail;
            if (!detail) return;
            if (Number(detail.companyId) !== companyIdState || Number(detail.annualId) !== annualIdState) {
                return;
            }
            void silentRefreshAfterWpSave();
        };
        window.addEventListener(FS_ADJUSTMENT_SILENT_REFRESH_EVENT, onSilentRefresh);
        return () => window.removeEventListener(FS_ADJUSTMENT_SILENT_REFRESH_EVENT, onSilentRefresh);
    }, [companyIdState, annualIdState, silentRefreshAfterWpSave]);

    // 同步数据到 fsCategoryStore - 已移除，因为新的 queryAdjustmentList 不再单独返回 headers

    const syncAdjustmentDataToStore = useCallback((data: typeof adjustmentData) => {
        try {
            let record = data && typeof data === 'object' ? data : null;
            if (record) {
                const aid = Number(record.annualId);
                const annual = aid > 0
                    ? useCompanyAnnualStore.getState().byAnnualId[aid]
                    : undefined;
                // CompanyAnnual 首年审批且未勾选 multi-year audit → 与 resolveAuditSpanType 一致走 SINGLE
                if (Number(annual?.firstAudit ?? 0) === 1 && annual?.isMultiYearAudit === false) {
                    record = { ...record, spanType: 'SINGLE' };
                }
            }
            useFsAdjustStore.getState().syncFromQueryData(record);
            prevAdjustmentDataRef.current = data;
        } catch (e) {
            console.error('[FsAdjustment] Failed to sync adjustment data:', e);
        }
    }, []);

    // Sync before paint when query data identity changes (fast path while mounted)
    useLayoutEffect(() => {
        if (prevAdjustmentDataRef.current === adjustmentData) {
            return;
        }
        syncAdjustmentDataToStore(adjustmentData);
    }, [adjustmentData, syncAdjustmentDataToStore]);

    // Re-hydrate after clear effect wiped store (Back → re-enter same record with RQ cache)
    useEffect(() => {
        const storeRecord = useFsAdjustStore.getState().adjustRecord;
        if (prevAdjustmentDataRef.current === adjustmentData && storeRecord) {
            return;
        }
        syncAdjustmentDataToStore(adjustmentData);
    }, [adjustmentData, companyId, annualId, syncAdjustmentDataToStore]);

    // 同步 rounding 数据到 store（后台）；不参与首屏 Skeleton 判定
    useEffect(() => {
        if (isRoundingPlaceholder && isRoundingFetching) return;
        useFsAdjustStore.getState().setRoundingGroup(roundingGroup ?? null);
    }, [roundingGroup, isRoundingPlaceholder, isRoundingFetching]);

    // draftFinalAmount 在首屏绘制后后台写入 Redis，绝不阻塞组件渲染。
    // 同一 draftKey 只触发一次；并行 effect / 多次 list invalidate 会抢占同一 key，避免 TAX_SYNC 风暴。
    const draftFinalAmountKeyRef = useRef<string | null>(null);
    const draftFinalAmountInFlightRef = useRef<Promise<boolean> | null>(null);
    useEffect(() => {
        draftFinalAmountKeyRef.current = null;
        draftFinalAmountInFlightRef.current = null;
    }, [companyIdState, annualIdState]);
    useEffect(() => {
        // list 未就绪时无法组装 draft；rounding 仍用 placeholder 时尽量等真实数据，避免无 rounding 先 draft 再重打
        if (!adjustmentData || adjustmentLoading || adjustmentFetching) return;
        if (isRoundingPlaceholder && isRoundingFetching) return;

        const record = useFsAdjustStore.getState().adjustRecord;
        if (!record) return;

        const targetCount = roundingGroup?.targets?.length ?? 0;
        // Include a stable stamp from the latest list payload so EXG/Tax 更新后仍会再 draft 一次，
        // 但同一次 refetch 不会并行打出多份 draftFinalAmount。
        const listStamp = Array.isArray((adjustmentData as any)?.headers)
            ? (adjustmentData as any).headers.length
            : (adjustmentData as any)?.updateTime
                ?? (adjustmentData as any)?.id
                ?? 'list';
        const draftKey = `${record.companyId}-${record.annualId}-${targetCount}-${listStamp}`;
        if (draftFinalAmountKeyRef.current === draftKey) return;
        // Claim before scheduling async work so concurrent effect runs see the same key.
        draftFinalAmountKeyRef.current = draftKey;

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const runDraftSync = () => {
            if (cancelled) return;
            if (draftFinalAmountInFlightRef.current) {
                void draftFinalAmountInFlightRef.current;
                return;
            }
            const pending = useFsAdjustStore.getState().syncDraftFinalAmount().then((ok) => {
                if (!ok && !cancelled && draftFinalAmountKeyRef.current === draftKey) {
                    draftFinalAmountKeyRef.current = null;
                }
                return ok;
            }).finally(() => {
                if (draftFinalAmountInFlightRef.current === pending) {
                    draftFinalAmountInFlightRef.current = null;
                }
            });
            draftFinalAmountInFlightRef.current = pending;
        };

        // 保存后尽快写 draft，不再等 requestIdleCallback（最长 1.5s）
        timeoutId = setTimeout(runDraftSync, 0);

        return () => {
            cancelled = true;
            if (timeoutId != null) clearTimeout(timeoutId);
        };
    }, [adjustmentData, adjustmentLoading, adjustmentFetching, isRoundingPlaceholder, isRoundingFetching, roundingGroup]);

    // RoundingAdjustment 保存/删除后重新写入 draftFinalAmount 缓存（同样后台执行，不挡 UI）
    useEffect(() => {
        const unsubscribe = subscribeRoundingSaved(() => {
            draftFinalAmountKeyRef.current = null;
            const latestRounding = queryClient.getQueryData<typeof roundingGroup>(
                ['rounding-adjust', annualIdState]
            );
            if (latestRounding) {
                useFsAdjustStore.getState().setRoundingGroup(latestRounding);
            }
            const schedule = () => {
                void useFsAdjustStore.getState().syncDraftFinalAmount();
            };
            if (typeof window !== 'undefined' && typeof (window as any).requestIdleCallback === 'function') {
                (window as any).requestIdleCallback(schedule, {timeout: 1500});
            } else {
                setTimeout(schedule, 0);
            }
        });
        return unsubscribe;
    }, [queryClient, annualIdState]);

    // Mirror subsequent context updates into store
    useEffect(() => {
        try {
            useFsCategoryStore.getState().setContext({companyId: companyIdState, companyName: companyName ?? null});
        } catch {
        }
    }, [companyIdState, companyName]);

    // Hold global allowed schedule codes and schedule name map derived from all headers
    const [allAllowed, setAllAllowed] = useState<Set<string>>(new Set());
    const allowedForTabs = useMemo(() => (showAllSchedules ? undefined : allAllowed), [showAllSchedules, allAllowed]);
    const adjustmentStore = useAdjustmentStore();
    const hasUpdated = adjustmentStore.hasUpdated;
    const setHasUpdated = adjustmentStore.setHasUpdated;
    const currentMode = adjustmentStore.currentMode;
    const entriesInitializedRef = useRef<boolean>(false);

    // entries 有变化时标记为已更新
    useEffect(() => {
        // 跳過首次初始化（後端加載 entries）
        // 首次之後的變更才認為是“用戶更新”
        if (!(entriesInitializedRef.current)) {
            entriesInitializedRef.current = true;
            return;
        }
        setHasUpdated(true);
    }, [adjustmentStore.entries]);

    // Auto saveDraft 功能已移除

    // Mirror allowed filter toggle into store so child components can fallback without props
    useEffect(() => {
        try {
            useFsCategoryStore.getState().setAdjustments({allowedScheduleCodes: allowedForTabs as any});
        } catch {
        }
    }, [allowedForTabs]);

    // Local dynamic dialog launcher state (click schedule chip in BS/PL tables)
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogCode, setDialogCode] = useState<string | null>(null);
    const [dialogPayload, setDialogPayload] = useState<Record<string, any>>({});

    // 初始化 currentAnnual（annualIdState 变化时），卸载时清空
    useEffect(() => {
        if (annualIdState > 0) {
            useCompanyAnnualStore.getState().initCurrentAnnual(annualIdState).catch(() => {});
        }
        return () => {
            useCompanyAnnualStore.getState().clearCurrentAnnual();
        };
    }, [annualIdState]);

    // firstAudit / isMultiYearAudit：CompanyAnnual 更新后同步影响 MULTI vs SINGLE 渲染
    // Selectors must return primitives — returning a new object each run triggers infinite Zustand re-renders.
    const firstYearAudit = useCompanyAnnualStore((s) => {
        const id = annualIdState > 0 ? annualIdState : (s.currentAnnualId ?? 0);
        if (id <= 0) return false;
        return Number(s.byAnnualId[id]?.firstAudit ?? 0) === 1;
    });
    const forceEngagementSingle = useCompanyAnnualStore((s) => {
        const id = annualIdState > 0 ? annualIdState : (s.currentAnnualId ?? 0);
        if (id <= 0) return false;
        const annual = s.byAnnualId[id];
        return Number(annual?.firstAudit ?? 0) === 1 && annual?.isMultiYearAudit === false;
    });
    const annualSpanKey = useCompanyAnnualStore((s) => {
        const id = annualIdState > 0 ? annualIdState : (s.currentAnnualId ?? 0);
        if (id <= 0) return '';
        const annual = s.byAnnualId[id];
        return `${annual?.firstAudit}|${annual?.isMultiYearAudit}|${annual?.periodFrom}|${annual?.periodTo}`;
    });

    // Phase 4: BS/PL 一级 Tab + PL Period Tab（仅 MULTI）
    const [fsTabKey, setFsTabKey] = useState<string>('BS');
    const [plPeriodKey, setPlPeriodKey] = useState<string>('');
    /** MULTI PL：多 Tab vs Total 汇总（TotalSingle）；初始化默认 Total 汇总视图（Client 跨片 Σ + 按需拆分） */
    const [plMultiViewMode, setPlMultiViewMode] = useState<PlMultiViewMode>('total');
    const [clientSlices, setClientSlices] = useState<AuditPeriodSlice[]>([]);
    const [clientSpanType, setClientSpanType] = useState<'SINGLE' | 'MULTI' | null>(null);

    // Prefer queryAdjustmentList.periods[]; fallback getAuditPeriodSpan
    const plSlices: AuditPeriodSlice[] = useMemo(() => {
        if (forceEngagementSingle) {
            return [];
        }
        if (adjustmentData?.spanType === 'MULTI' && Array.isArray(adjustmentData.periods) && adjustmentData.periods.length > 0) {
            return adjustmentData.periods.map((p) => ({
                index: p.index,
                periodFrom: p.periodFrom,
                periodTo: p.periodTo,
                yearOfAssessment: p.yearOfAssessment ?? null,
                key: p.key,
                label: p.label || p.yearOfAssessment || `${p.periodFrom} – ${p.periodTo}`,
            }));
        }
        if (clientSpanType === 'MULTI' && clientSlices.length > 0) {
            return clientSlices;
        }
        return [];
    }, [adjustmentData?.spanType, adjustmentData?.periods, clientSpanType, clientSlices, forceEngagementSingle]);

    const isPlMulti = !forceEngagementSingle
        && (adjustmentData?.spanType === 'MULTI' || clientSpanType === 'MULTI')
        && plSlices.length >= 2;

    useEffect(() => {
        if (annualIdState <= 0) return;
        let cancelled = false;
        useCompanyAnnualStore.getState().getAuditPeriodSpan(annualIdState, {
            inCorpDate: inCorpDateState,
        }).then((span) => {
            if (cancelled) return;
            setClientSpanType(span.type);
            setClientSlices(span.slices || []);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [annualIdState, inCorpDateState, annualSpanKey]);

    useEffect(() => {
        if (!isPlMulti || plSlices.length === 0) return;
        if (!plPeriodKey || !plSlices.some((s) => s.key === plPeriodKey)) {
            setPlPeriodKey(plSlices[0].key);
        }
    }, [isPlMulti, plSlices, plPeriodKey]);

    const activePlSlice = useMemo(() => {
        if (!isPlMulti || plSlices.length === 0) return null;
        return plSlices.find((s) => s.key === plPeriodKey) || plSlices[0];
    }, [isPlMulti, plSlices, plPeriodKey]);

    const buildPlPeriodPayload = useCallback((
        slice: AuditPeriodSlice | null | undefined,
        code?: string,
    ) => {
        if (!slice && plSlices.length === 0) return {};
        const spanType: 'SINGLE' | 'MULTI' = isPlMulti ? 'MULTI' : 'SINGLE';
        const tabMode = resolveWpPeriodTabMode({
            code: code || '',
            statement: 'PL',
            spanType,
        });
        // QXI1 etc.: WP-inner Period Tabs so each YOA can set bank (= deduct in tax com) separately
        if (tabMode === 'multi' && plSlices.length >= 2) {
            const start = slice || plSlices[0];
            const periodScope: WpPeriodScope = sliceToPeriodScope(start, {
                isFirstSlice: plSlices[0]?.key === start.key,
            });
            return {
                spanType: 'MULTI' as const,
                periods: plSlices,
                periodFrom: start.periodFrom,
                periodTo: start.periodTo,
                sliceKey: start.key,
                yearOfAssessment: start.yearOfAssessment,
                periodScope,
                wpPeriodTabMode: 'multi' as const,
                wpPerCheckFollowActiveTab: true,
            };
        }
        if (!slice) return {};
        const periodScope: WpPeriodScope = {
            periodFrom: slice.periodFrom,
            periodTo: slice.periodTo,
            yearOfAssessment: slice.yearOfAssessment,
            sliceKey: slice.key,
            isFirstSlice: plSlices[0]?.key === slice.key,
        };
        return {
            periodFrom: slice.periodFrom,
            periodTo: slice.periodTo,
            sliceKey: slice.key,
            yearOfAssessment: slice.yearOfAssessment,
            periodScope,
            /** 其余 PL（含 PL-TAX）：Period Tab 已在 PL 层；WP 内不再套年 Tab */
            wpPeriodTabMode: 'single' as const,
            spanType: 'MULTI' as const,
            periods: plSlices,
        };
    }, [plSlices, isPlMulti]);

    const engagementSpanType: 'SINGLE' | 'MULTI' = useMemo(() => {
        if (forceEngagementSingle) return 'SINGLE';
        if (adjustmentData?.spanType === 'MULTI' || clientSpanType === 'MULTI') return 'MULTI';
        if (isPlMulti) return 'MULTI';
        return 'SINGLE';
    }, [adjustmentData?.spanType, clientSpanType, isPlMulti, forceEngagementSingle]);

    const buildBsPeriodPayload = useCallback((code: string) => {
        const tabMode = resolveWpPeriodTabMode({
            code,
            statement: 'BS',
            spanType: engagementSpanType,
        });
        if (engagementSpanType !== 'MULTI' || plSlices.length === 0) {
            return {wpPeriodTabMode: 'single' as const};
        }
        if (tabMode === 'multi') {
            // Default to earliest slice; shell tabs switch periodScope
            const first = plSlices[0];
            const periodScope = sliceToPeriodScope(first);
            return {
                wpPeriodTabMode: 'multi' as const,
                spanType: 'MULTI' as const,
                periods: plSlices,
                periodFrom: first.periodFrom,
                periodTo: first.periodTo,
                sliceKey: first.key,
                yearOfAssessment: first.yearOfAssessment,
                periodScope,
            };
        }
        // Non-whitelist BS under MULTI: no WP inner tabs, but periods for Drawer §5.7
        return {
            wpPeriodTabMode: 'single' as const,
            spanType: 'MULTI' as const,
            periods: plSlices,
        };
    }, [engagementSpanType, plSlices]);

    // Ref-based stable onRequestOpenCode: 跨组件跳转时始终可用，支持双向导航
    const openCodeRef = useRef<(nextCode: string) => void>(() => {});
    const dialogPeriodPayloadRef = useRef<Record<string, any>>({});
    openCodeRef.current = (nextCode: string) => {
        const normalized = String(nextCode || '').trim().toUpperCase();
        if (!normalized) return;
        const fsLine = useFsAdjustStore.getState().getLineByCode(normalized);
        const nextStatement: 'BS' | 'PL' = normalized.startsWith('PL-') ? 'PL' : 'BS';
        // 关闭 special dialog（如果打开的话）
        setSpecialDialogType(null);
        setSpecialDialogPayload(null);
        // PL：保留当前 PL periodScope；BS：按白名单 resolve multi/single
        let periodFields: Record<string, any> = {};
        if (nextStatement === 'PL') {
            periodFields = dialogPeriodPayloadRef.current?.periodScope
                ? dialogPeriodPayloadRef.current
                : buildPlPeriodPayload(activePlSlice, normalized);
        } else {
            periodFields = buildBsPeriodPayload(normalized);
        }
        dialogPeriodPayloadRef.current = periodFields;
        setDialogCode(normalized);
        setDialogPayload({
            companyId: companyIdState,
            annualId: annualIdState,
            statement: nextStatement,
            fsId: Number((fsLine as any)?.fsId || 0),
            itemName: (fsLine as any)?.name,
            code: normalized,
            line: fsLine,
            workflow,
            onRequestOpenCode: (code: string) => openCodeRef.current(code),
            ...periodFields,
        });
        setDialogOpen(true);
    };
    const stableOpenCode = useCallback((nextCode: string) => openCodeRef.current(nextCode), []);

    useEffect(() => {
        useAdjustDraftStore.getState().setOnRequestOpenWpCode(stableOpenCode);
        return () => useAdjustDraftStore.getState().setOnRequestOpenWpCode(null);
    }, [stableOpenCode]);

    // Loading state for adjustment cell click
    const [adjustmentCellLoading, setAdjustmentCellLoading] = useState<boolean>(false);

    // Shared Adjust Total mode — sync BS/PL.
    // Init: max(manual + Auto cols) >= 5 → Adjust Total; else detail.
    // WHY: first child report is often 0 (store sync after mount); must not lock on 0.
    const [adjustTotalMode, setAdjustTotalMode] = useState<boolean>(false);
    const adjustTotalUserTouchedRef = useRef(false);
    const adjustTotalInitAppliedRef = useRef(false);
    const [bsAdjustColCount, setBsAdjustColCount] = useState<number | null>(null);
    const [plAdjustColCount, setPlAdjustColCount] = useState<number | null>(null);

    const handleAdjustTotalModeChange = useCallback((mode: boolean) => {
        adjustTotalUserTouchedRef.current = true;
        setAdjustTotalMode(mode);
    }, []);

    const handleBsAdjustColumnCountChange = useCallback((count: number) => {
        if (adjustTotalUserTouchedRef.current || adjustTotalInitAppliedRef.current) return;
        setBsAdjustColCount((prev) => (prev == null ? count : Math.max(prev, count)));
        // Apply as soon as either statement reports >= 5 (manual + Auto)
        if (count >= 5) {
            adjustTotalInitAppliedRef.current = true;
            setAdjustTotalMode(true);
        }
    }, []);

    const handlePlAdjustColumnCountChange = useCallback((count: number) => {
        if (adjustTotalUserTouchedRef.current || adjustTotalInitAppliedRef.current) return;
        setPlAdjustColCount((prev) => (prev == null ? count : Math.max(prev, count)));
        if (count >= 5) {
            adjustTotalInitAppliedRef.current = true;
            setAdjustTotalMode(true);
        }
    }, []);

    // Reset init when switching company / annual
    useEffect(() => {
        adjustTotalUserTouchedRef.current = false;
        adjustTotalInitAppliedRef.current = false;
        setBsAdjustColCount(null);
        setPlAdjustColCount(null);
        setAdjustTotalMode(false);
    }, [companyIdState, annualIdState]);

    useLayoutEffect(() => {
        if (adjustTotalUserTouchedRef.current || adjustTotalInitAppliedRef.current) return;
        if (bsAdjustColCount == null && plAdjustColCount == null) return;
        const maxCount = Math.max(bsAdjustColCount ?? 0, plAdjustColCount ?? 0);
        // Still empty trees — wait for real column defs after store sync
        if (maxCount === 0) return;
        // >= 5: fold immediately (do not wait for the other statement)
        if (maxCount >= 5) {
            adjustTotalInitAppliedRef.current = true;
            setAdjustTotalMode(true);
            return;
        }
        // < 5: lock detail only after list settled and both sides reported
        if (
            !adjustmentLoading
            && !adjustmentFetching
            && bsAdjustColCount != null
            && plAdjustColCount != null
        ) {
            adjustTotalInitAppliedRef.current = true;
            setAdjustTotalMode(false);
        }
    }, [bsAdjustColCount, plAdjustColCount, adjustmentLoading, adjustmentFetching]);

    // Shared Audited Rounded mode state - synchronized between BalanceSheet and ProfitLoss
    const [auditedRounded, setAuditedRounded] = useState<AuditedMode>('normal');

    const handleAuditedRoundedChange = useCallback((mode: AuditedMode) => {
        if (mode === 'round' && auditedRounded === 'normal') {
            // PL Total 汇总视图：跨片 Σ 只读，不打开 RoundingAdjustment（各片已在 multiple 下编辑）
            const skipRoundingGate = isPlMulti && plMultiViewMode === 'total' && fsTabKey === 'PL';
            if (!skipRoundingGate) {
                requestAuditedRoundingGate();
            }
        }
        setAuditedRounded(mode);
    }, [auditedRounded, isPlMulti, plMultiViewMode, fsTabKey]);

    // Handler for clicking on adjustment cells in BalanceSheet/ProfitLoss
    const handleAdjustmentCellClick = async (headerId: string, refNo: string, item: any) => {
        if (adjustmentCellLoading) return;

        try {
            setAdjustmentCellLoading(true);

            // Call API to get full adjustment data with working papers
            const headerData = await getAdjustmentWithWorkingPapers(headerId);

            if (!headerData) {
                setSnack({open: true, message: 'Failed to load adjustment data', severity: 'error'});
                return;
            }

            const fsAdjustState = useFsAdjustStore.getState();
            const adjustDraftStore = useAdjustDraftStore.getState();
            adjustDraftStore.cleanAll();
            adjustDraftStore.loadFromHeader(headerData, (code: string) => fsAdjustState.getLineByCode(code));
            adjustDraftStore.syncFromPaperDetails();
            adjustDraftStore.setAdjustTabsDialogOpen(true);

        } catch (error: any) {
            console.error('[FsAdjustment] Failed to load adjustment:', error);
            setSnack({open: true, message: error?.message || 'Failed to load adjustment data', severity: 'error'});
        } finally {
            setAdjustmentCellLoading(false);
        }
    };

    // Cleanup on unmount like FsDetail to avoid stale flashes
    useEffect(() => {
        return () => {
            try {
                useFsCategoryStore.getState().reset();
                useFsAdjustStore.getState().clear();
            } catch {
            }
        };
    }, []);

    // Auto set active header id - 已移除，新的 queryAdjustmentList 不再单独返回 headers

    const [saving, setSaving] = useState(false);
    const [exportingIssues, setExportingIssues] = useState(false);
    const [openingPreview, setOpeningPreview] = useState(false);
    const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
    const [snack, setSnack] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'info' | 'warning' | 'error'
    } | null>(null);

    /** WP save results (via DialogLauncher). SnackbarField only opens after registry WP closes. */
    const handleWpSnackbar = useCallback<ShowSnackbarFn>((message, severity) => {
        setSnack({open: true, message, severity});
    }, []);

    const itemAliasEditEnabled = workflow === 'audit';

    /** In-flight leaf rename saves — Back must await these or re-enter may show old names */
    const pendingAliasSavesRef = useRef(new Set<Promise<unknown>>());

    const handleItemAliasChange = useCallback((payload: {
        fsId: number;
        code: string;
        statement: 'BS' | 'PL';
        displayName: string;
    }) => {
        const task = (async () => {
            const codeKey = String(payload.code || '').trim().toUpperCase();
            const prevLine = useFsAdjustStore.getState().getLineByCode(codeKey)
                ?? useFsCategoryStore.getState().getLineByCode(codeKey);
            const previous = {
                name: (prevLine as {name?: string | null} | null)?.name ?? null,
                aliasEn: (prevLine as {aliasEn?: string | null} | null)?.aliasEn ?? null,
                aliasZh: (prevLine as {aliasZh?: string | null} | null)?.aliasZh ?? null,
            };

            // Optimistic: render new name immediately (no wait for API)
            const optimistic = {
                name: payload.displayName,
                aliasEn: payload.displayName,
                aliasZh: null as string | null,
            };
            useFsAdjustStore.getState().patchItemAlias(payload.code, optimistic);
            useFsCategoryStore.getState().patchLineAlias(payload.code, optimistic);

            try {
                const result = await AuditCompanyFSCategoryService.updateAliasFromAdjustment({
                    companyId: companyIdState,
                    annualId: annualIdState,
                    statement: payload.statement,
                    fsId: payload.fsId,
                    code: payload.code,
                    displayName: payload.displayName,
                });
                const patch = {
                    name: result.displayName ?? payload.displayName,
                    aliasEn: result.aliasEn ?? null,
                    aliasZh: result.aliasZh ?? null,
                };
                useFsAdjustStore.getState().patchItemAlias(payload.code, patch);
                useFsCategoryStore.getState().patchLineAlias(payload.code, patch);
                // Await list refresh so Back's pendingAliasSaves wait includes RQ update
                await scheduleCoalescedAdjustmentListRefresh(
                    companyIdState,
                    annualIdState,
                    () => refetchAdjustmentList(),
                );
                // success: no snackbar
            } catch (e: unknown) {
                useFsAdjustStore.getState().patchItemAlias(payload.code, previous);
                useFsCategoryStore.getState().patchLineAlias(payload.code, previous);
                const msg = e instanceof Error ? e.message : 'Failed to update item name.';
                setSnack({open: true, message: msg, severity: 'warning'});
                throw e;
            }
        })();

        pendingAliasSavesRef.current.add(task);
        void task.finally(() => {
            pendingAliasSavesRef.current.delete(task);
        });
        return task;
    }, [companyIdState, annualIdState, refetchAdjustmentList]);

    /** BankLoan / VehicleLoan special entry — queue until that dialog closes. */
    const pendingSpecialSnackRef = useRef<{
        message: string;
        severity: 'warning' | 'error' | 'success';
    } | null>(null);
    const handleSpecialWpSnackbar = useCallback<ShowSnackbarFn>((message, severity) => {
        pendingSpecialSnackRef.current = {message, severity};
    }, []);
    const prevSpecialDialogRef = useRef(specialDialogType);
    useEffect(() => {
        const wasOpen = prevSpecialDialogRef.current != null;
        prevSpecialDialogRef.current = specialDialogType;
        if (!wasOpen || specialDialogType != null) return;
        const pending = pendingSpecialSnackRef.current;
        pendingSpecialSnackRef.current = null;
        if (!pending) return;
        queueMicrotask(() => {
            setSnack({open: true, message: pending.message, severity: pending.severity});
        });
    }, [specialDialogType]);

    const saveDraftFinalAmountWithRounding = useCallback(async (
        yearOfAssessment?: string | null,
    ): Promise<boolean> => {
        // 调用 draftFinalAmount 前确保 rounding 数据已加载
        let latestRounding = queryClient.getQueryData<typeof roundingGroup>(
            ['rounding-adjust', annualIdState]
        );
        if (!latestRounding && annualIdState) {
            const result = await refetchRoundingGroup();
            latestRounding = result.data;
        }
        useFsAdjustStore.getState().setRoundingGroup(latestRounding ?? null);
        draftFinalAmountKeyRef.current = null;
        return useFsAdjustStore.getState().syncDraftFinalAmount(yearOfAssessment);
    }, [queryClient, annualIdState, refetchRoundingGroup]);

    const openPreviewDialog = useCallback(() => {
        setPreviewDialogOpen(true);
    }, []);

    const handlePreviewClick = useCallback(() => {
        if (openingPreview) return;
        const switchingToRound = auditedRounded !== 'round';
        if (switchingToRound) {
            setAuditedRounded('round');
        }
        requestTaxRoundingGate({
            onBalanced: () => {
                void (async () => {
                    try {
                        setOpeningPreview(true);
                        const saved = await saveDraftFinalAmountWithRounding();
                        if (!saved) {
                            setSnack({open: true, message: 'Failed to save draft final amount.', severity: 'error'});
                            return;
                        }
                        openPreviewDialog();
                    } finally {
                        setOpeningPreview(false);
                    }
                })();
            },
            onCancelled: () => setSnack({
                open: true,
                message: 'Please apply rounded item first!',
                severity: 'warning',
            }),
        });
    }, [
        auditedRounded, openingPreview, saveDraftFinalAmountWithRounding, openPreviewDialog,
    ]);

    const handleExportIssuesOutstanding = useCallback(async () => {
        if (exportingIssues) return;
        try {
            setExportingIssues(true);
            const {blob, filename} = await exportFsWpIssuesExcel(companyIdState, annualIdState, {
                outstandingOnly: true,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setSnack({open: true, message: 'Export OUTSTANDING TO CLIENT Successful!', severity: 'success'});
        } catch (e: any) {
            setSnack({open: true, message: e?.message || 'Export OUTSTANDING TO CLIENT failed.', severity: 'error'});
        } finally {
            setExportingIssues(false);
        }
    }, [exportingIssues, companyIdState, annualIdState]);

    const handleOpenIssueSchedule = useCallback((code: string, statement: 'BS' | 'PL', _fsId: number) => {
        setFsTabKey(statement === 'PL' ? 'PL' : 'BS');
        openCodeRef.current(code);
    }, []);

    const handleBack = async () => {
        // Flush in-progress rename (blur → commit) then wait for API before clearing stores.
        // Otherwise optimistic alias is wiped while POST is still in flight → re-enter shows old name.
        const cid = companyIdState;
        const aid = annualIdState;
        try {
            const active = document.activeElement;
            if (active instanceof HTMLElement) {
                active.blur();
            }
            await new Promise<void>((resolve) => {
                window.setTimeout(resolve, 0);
            });
            if (pendingAliasSavesRef.current.size > 0) {
                await Promise.allSettled([...pendingAliasSavesRef.current]);
            }
        } catch {
        }

        // Drop stale RQ list cache + mark force-refresh on re-enter (60s staleTime otherwise
        // can rehydrate pre-rename names even after POST succeeded).
        try {
            if (cid && aid) {
                clearFsAdjustmentCachesForAnnual(cid, aid, queryClient);
            }
        } catch {
        }

        try {
            // 触发清空 BalanceSheet / ProfitLoss 中的 fsOpeningAmounts
            adjustmentStore.triggerFsOpeningReset();

            adjustmentStore.clearMode2Assist();
            adjustmentStore.clearMode3Assist();
            adjustmentStore.clearAll();
            adjustmentStore.setHeader(null);
            adjustmentStore.setActiveHeaderId(null);

            useAdjustDraftStore.getState().cleanAll();
            useFsAdjustStore.getState().clear();
            useFsCategoryStore.getState().reset();
        } catch {
        }
        // Clear internal values
        setCompanyIdState(0 as number);
        setAnnualIdState(0 as number);
        try {
            onRequestListRefresh?.();
        } catch {
        }
        if (onBack) onBack();
    };

    // 拦截浏览器后退按钮（鼠标返回），触发 handleBack 逻辑
    const handleBackRef = useRef<() => void | Promise<void>>(handleBack);
    handleBackRef.current = handleBack;

    useEffect(() => {
        // 推入一个 history state，使得浏览器后退按钮触发 popstate 而非离开页面
        window.history.pushState({fsAdjustment: true}, '');

        const onPopState = (_e: PopStateEvent) => {
            // 触发组件的 handleBack 逻辑
            void handleBackRef.current();
        };

        window.addEventListener('popstate', onPopState);

        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, []);

    const handleModify = async () => {
        try {
            setSaving(true);
            
            // 根据 workflow 决定目标状态
            const targetStatus = workflow === 'account'
                ? AnnualFsStatusEnum.ACCOUNT_EDITING    // Account: 3 → 1
                : AnnualFsStatusEnum.AUDIT_EDITING;     // Audit:   3 → 2
            
            await changeFsStatus(companyIdState, annualIdState, targetStatus);
            setSnack({open: true, message: 'Back to editing successful!', severity: 'success'});

            // Modify 后清除 L1 React Query + L2 Zustand 前端缓存，避免再次进入命中旧数据
            clearFsAdjustmentCachesForAnnual(companyIdState, annualIdState, queryClient);

            // 导航行为统一：回到 AuditAdjustmentList
            if (onAfterModifySuccess) {
                onAfterModifySuccess();
            }
            await handleBack();
        } catch (e: any) {
            setSnack({open: true, message: e?.message || 'Back to editing failed.', severity: 'error'});
        } finally {
            setSaving(false);
        }
    }

    const scheduleClick = async (item: any, statement: 'BS' | 'PL', plSlice?: AuditPeriodSlice | null) => {
        // FS_TO_FS 模式下不响应 chip 点击
        if (currentMode === AdjustmentMode.ADJUST_FS_TO_FS) {
            return;
        }

        // Use code directly, not scheduleCode
        const code = String(item?.code || '').trim().toUpperCase();
        if (!code) return;

        const incomingComputedAudited = item?.computedAudited;

        // ✅ 计算 Audited 值 = Client (openingAmount) + Adjust Total
        const clientAmount = Number(item?.openingAmount ?? item?.amount ?? 0);
        const adjustTotal = Array.isArray(item?.adjustment)
            ? item.adjustment.reduce((sum: number, adj: any) => sum + (adj?.adjustment ?? 0), 0)
            : 0;
        const calculatedAudited = clientAmount + adjustTotal;

        const computedAuditedToUse = (incomingComputedAudited !== undefined && incomingComputedAudited !== null)
            ? (Number(incomingComputedAudited) || 0)
            : calculatedAudited;

        const isPlTotalView = statement === 'PL' && isPlMulti && plMultiViewMode === 'total';

        // Phase 4/5: PL → periodScope (single WP tab); BS 白名单 → wpPeriodTabMode multi
        // Total 汇总：WP 内多年 Tab（同 PPE multi），按年展示/编辑；不再用 PL_TOTAL 只读 Σ
        let periodFields: Record<string, any> = {};
        if (statement === 'PL' && isPlTotalView) {
            const first = plSlices[0];
            const last = plSlices[plSlices.length - 1] || first;
            const periodScope: WpPeriodScope = sliceToPeriodScope(first, {
                isFirstSlice: true,
            });
            periodFields = {
                spanType: 'MULTI',
                periods: plSlices,
                periodFrom: first?.periodFrom,
                periodTo: last?.periodTo,
                sliceKey: first?.key,
                yearOfAssessment: first?.yearOfAssessment,
                periodScope,
                /** Same shell as BS whitelist / PPE：DialogLauncher Period Tabs */
                wpPeriodTabMode: 'multi' as const,
                /** WpPerCheck Total final follows active YOA Tab (not last-tab span endpoints) */
                wpPerCheckFollowActiveTab: true,
            };
            dialogPeriodPayloadRef.current = periodFields;
        } else {
            const sliceForPl = statement === 'PL' ? (plSlice || activePlSlice) : null;
            periodFields = statement === 'PL'
                ? buildPlPeriodPayload(sliceForPl, code)
                : buildBsPeriodPayload(code);
            if (statement === 'PL') {
                dialogPeriodPayloadRef.current = periodFields;
            } else if ((periodFields as any).wpPeriodTabMode === 'multi') {
                dialogPeriodPayloadRef.current = periodFields;
            } else {
                // Preserve MULTI periods for Drawer locks when navigating from non-multi BS
                dialogPeriodPayloadRef.current = periodFields;
            }
        }

        setDialogCode(code);
        setDialogPayload({
            companyId: companyIdState,
            annualId: annualIdState,
            statement: statement,
            fsId: Number(item?.fsId || 0),
            itemName: item?.name, // Use name field instead of scheduleName
            code: code, // Pass the new code field
            line: {
                ...item,
                // Prefer PL-displayed Last Year (MULTI prior-tab Audited) for WP lastYearFinal seed
                lastYearAudited: item?.computedLastYearAudited ?? item?.lastYearAudited,
                computedLastYearAudited: item?.computedLastYearAudited,
            },
            calculatedAudited: calculatedAudited, // ✅ 传递计算后的 Audited 值
            computedAudited: computedAuditedToUse, // ✅ 优先使用 ProfitLoss 计算值（如 PL-COT-PUR）
            workflow, // ✅ 透传 workflow 到 CommonWp
            onRequestOpenCode: stableOpenCode,
            ...periodFields,
            // 动态组件自行处理调整模式
        });
        setDialogOpen(true);
    };

    // 启动 FS_TO_FS 模式
    const startFsToFsMode = () => {
        // 初始化临时 header
        adjustmentStore.initTempHeader({
            companyId: companyIdState,
            annualId: annualIdState,
            adjustmentMode: AdjustmentMode.ADJUST_FS_TO_FS,
            preparedUserId: userStore.getCurrentUserId(),
        });

        // 设置 FS 编辑模式
        setFsOpeningEditMode(true);
    };

    // 处理 FS Opening 数据变化
    const handleFsOpeningDataChange = (rowData: Array<{
        fsId: number;
        code: string;
        scheduleName: string;
        statement: 'BS' | 'PL';
        entry?: string;
        adjustmentAmount: number;
        openingAmount: number;
        closingAmount: number;
        adjustHisAmount: number;
    }>) => {
        if (!rowData || rowData.length === 0) return;

        // 构建新的 lines
        const newLines = rowData.map(row => ({
            companyId: companyIdState,
            annualId: annualIdState,
            fsId: row.fsId,
            code: row.code,
            scheduleCode: row.code, // Keep scheduleCode for backend compatibility
            scheduleName: row.scheduleName,
            statement: row.statement,
            entry: row.entry,          // 节点原始 entry (Dr/Cr)
            adjustmentAmount: row.adjustmentAmount,
            openingAmount: row.openingAmount,      // Client 值
            closingAmount: row.closingAmount,      // Audited 值
            adjustHisAmount: row.adjustHisAmount,  // 历史调整值
            currency: 'HKD',
            details: [], // FS_OPENING 模式 detail 为空
        }));

        // 获取当前 statement 类型（BS 或 PL）
        const currentStatement = newLines[0]?.statement;

        // 更新 adjustmentStore 的 header
        const header = adjustmentStore.header;
        if (header) {
            const existingLines = (header as any)?.lines || [];

            // 保留其他 statement 的 lines，替换当前 statement 的 lines
            const otherStatementLines = existingLines.filter((line: any) => line.statement !== currentStatement);
            const mergedLines = [...otherStatementLines, ...newLines];

            adjustmentStore.setHeader({
                ...header,
                lines: mergedLines as any,
            });
            setHasUpdated(true);

            // 计算 Dr/Cr 平衡错误
            calculateFsOpeningError(mergedLines);
        }
    };

    // 计算 FS_TO_FS 模式下的 Dr/Cr 平衡错误
    const calculateFsOpeningError = (lines: any[]) => {
        const fsAdjustState = useFsAdjustStore.getState();
        
        // 检查是否所有调整值都为 0（没有调整）
        const allZero = lines.every((line: any) => Math.abs(Number(line.adjustmentAmount) || 0) < 0.01);
        setAllExclusiveCodesZero(allZero);
        if (allZero) {
            setFsOpeningError(null);
            return;
        }

        // 使用绝对值计算 Dr 和 Cr 总和
        let drTotal = 0;
        let crTotal = 0;

        // Helper: 遍历 FS 树查找任意节点（包括 level 2 父节点）的 entry
        const findEntryInTree = (code: string): string | undefined => {
            const bsTree = fsAdjustState.getBsTree() || [];
            const plTree = fsAdjustState.getPlTree() || [];
            const target = code.trim().toUpperCase();
            const search = (nodes: any[]): string | undefined => {
                for (const node of nodes) {
                    const nodeCode = String(node?.code || '').trim().toUpperCase();
                    if (nodeCode === target && node.entry) return node.entry;
                    if (Array.isArray(node?.children)) {
                        const found = search(node.children);
                        if (found) return found;
                    }
                }
                return undefined;
            };
            return search([...bsTree, ...plTree]);
        };

        for (const line of lines) {
            const lineCode = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
            const adjustmentAmount = Number(line.adjustmentAmount) || 0;

            // 多级 fallback 获取节点 entry: line 自身 > fsAdjustStore(叶子) > FS树遍历(父节点)
            let entry = (line as any)?.entry
                || fsAdjustState.getLineByCode(lineCode)?.entry
                || findEntryInTree(lineCode);

            // 根据调整值的正负更新 entry
            if (entry) {
                if (entry === 'Dr') {
                    entry = adjustmentAmount < 0 ? 'Cr' : 'Dr';
                } else if (entry === 'Cr') {
                    entry = adjustmentAmount < 0 ? 'Dr' : 'Cr';
                }
            }

            // 使用绝对值累加
            if (entry === 'Dr') {
                drTotal += Math.abs(adjustmentAmount);
            } else if (entry === 'Cr') {
                crTotal += Math.abs(adjustmentAmount);
            }
        }

        // 平衡条件：Dr 总和应该等于 Cr 总和
        const balanceDifference = drTotal - crTotal;

        if (Math.abs(balanceDifference) > 0.01) {
            const absDiff = Math.abs(balanceDifference).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            let errorMessage = '';
            if (balanceDifference > 0) {
                errorMessage = `Unbalance of ${absDiff}, need a Cr. entry to balance.`;
            } else {
                errorMessage = `Unbalance of ${absDiff}, need a Dr. entry to balance.`;
            }
            setFsOpeningError(errorMessage);
        } else {
            setFsOpeningError(null);
        }
    };

    // 3.1 模式3（FS_OPENING）的 Adjustment 保存逻辑
    const handleAdjustment = async () => {
        if (!companyIdState || !annualIdState) return;

        const mode = adjustmentStore.currentMode;
        const header = adjustmentStore.header;

        if (mode !== AdjustmentMode.ADJUST_FS_TO_FS) {
            setSnack({
                open: true,
                message: 'This operation is not supported in the current mode.',
                severity: 'warning'
            });
            return;
        }

        if (!header) {
            setSnack({open: true, message: 'No data is pending submission.', severity: 'warning'});
            return;
        }

        // FS_OPENING 模式验证：至少需要 2 条 line
        const lines = (header as any)?.lines || [];
        if (lines.length < 2) {
            setSnack({
                open: true,
                message: 'FS Opening pattern requires at least 2 lines of data.',
                severity: 'error'
            });
            return;
        }

        const fsAdjustState = useFsAdjustStore.getState();

        // 检查是否所有调整值都为 0（没有调整）
        const allZero = lines.every((line: any) => Math.abs(Number(line.adjustmentAmount) || 0) < 0.01);
        if (allZero) {
            setSnack({
                open: true,
                message: 'No adjustments have been made. Please enter adjustment values.',
                severity: 'error'
            });
            return;
        }

        // Helper: 遍历 FS 树查找任意节点（包括 level 2 父节点）的 entry
        const findEntryInTree = (code: string): string | undefined => {
            const bsTree = fsAdjustState.getBsTree() || [];
            const plTree = fsAdjustState.getPlTree() || [];
            const target = code.trim().toUpperCase();
            const search = (nodes: any[]): string | undefined => {
                for (const node of nodes) {
                    const nodeCode = String(node?.code || '').trim().toUpperCase();
                    if (nodeCode === target && node.entry) return node.entry;
                    if (Array.isArray(node?.children)) {
                        const found = search(node.children);
                        if (found) return found;
                    }
                }
                return undefined;
            };
            return search([...bsTree, ...plTree]);
        };

        // ✅ 先构建提交数据（含 entry），再进行校验
        const orchestrateHeader: AuditAdjustHeader = {
            id: (header as any).id,
            companyId: companyIdState,
            annualId: annualIdState,
            adjustmentMode: AdjustmentMode.ADJUST_FS_TO_FS,
            refNo: (header as any).refNo || '',
            status: 0,
            preparedUserId: userStore.getCurrentUserId(),
            preparedDate: new Date().toISOString(),
            lines: lines.map((line: any): AuditAdjustLine => {
                const lineCode = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
                const rawAmount = Number(line.adjustmentAmount) || 0;
                // 多级 fallback 获取节点 entry: line 自身 > fsAdjustStore(叶子) > FS树遍历(父节点)
                const nodeEntry = (line as any)?.entry
                    || fsAdjustState.getLineByCode(lineCode)?.entry
                    || findEntryInTree(lineCode);
                // 步骤2: 根据节点 entry 和输入正负决定 line entry
                let lineEntry: string | undefined;
                if (nodeEntry) {
                    if (nodeEntry === 'Dr') {
                        lineEntry = rawAmount >= 0 ? 'Dr' : 'Cr';
                    } else {
                        lineEntry = rawAmount >= 0 ? 'Cr' : 'Dr';
                    }
                }
                return {
                    fsId: line.fsId || fsAdjustState.getLineByCode(lineCode)?.fsId || 0,
                    code: lineCode,
                    entry: lineEntry,
                    adjustmentAmount: Math.abs(rawAmount),
                    currency: line.currency || 'HKD',
                    details: [], // FS_TO_FS 模式下 details 为空
                };
            }),
        };

        // console.log('FS To FS mode: orchestrateHeader', orchestrateHeader);

        // ✅ 校验1: 所有 line 必须包含 entry
        const missingEntryLines = (orchestrateHeader.lines || []).filter(l => !l.entry);
        if (missingEntryLines.length > 0) {
            const codes = missingEntryLines.map(l => l.code).join(', ');
            setSnack({
                open: true,
                message: `Cannot determine Dr/Cr entry for: ${codes}. Please check the data.`,
                severity: 'error'
            });
            return;
        }

        // ✅ 校验2: Dr adjustmentAmount 总和 = Cr adjustmentAmount 总和
        let drTotal = 0;
        let crTotal = 0;
        const drItems: string[] = [];
        const crItems: string[] = [];

        for (const line of (orchestrateHeader.lines || [])) {
            const amount = line.adjustmentAmount || 0;
            const lineName = (lines.find((l: any) =>
                String(l?.code || l?.scheduleCode || '').trim() === line.code
            ) as any)?.scheduleName || line.code;
            const fmtAmount = amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

            if (line.entry === 'Dr') {
                drTotal += amount;
                drItems.push(`${lineName} ${fmtAmount}`);
            } else if (line.entry === 'Cr') {
                crTotal += amount;
                crItems.push(`${lineName} ${fmtAmount}`);
            }
        }

        const balanceDifference = drTotal - crTotal;
        if (Math.abs(balanceDifference) > 0.01) {
            const absDiff = Math.abs(balanceDifference).toLocaleString('en-US', {
                minimumFractionDigits: 2, maximumFractionDigits: 2
            });
            let errorMessage = balanceDifference > 0
                ? `Adjustment Unbalance of ${absDiff}, Please make a Cr. entry to balance it.`
                : `Adjustment Unbalance of ${absDiff}, Please make a Dr. entry to balance it.`;
            if (drItems.length > 0 || crItems.length > 0) {
                errorMessage += '\n';
                if (drItems.length > 0) errorMessage += `Dr. ${drItems.join(' | ')}`;
                if (crItems.length > 0) {
                    if (drItems.length > 0) errorMessage += '\n';
                    errorMessage += `      Cr. ${crItems.join(' | ')}`;
                }
            }
            setSnack({open: true, message: errorMessage, severity: 'error'});
            return;
        }

        try {
            setSaving(true);

            // ✅ 在调用后端接口之前，先清空 Adj 列中已有的数据
            adjustmentStore.triggerFsOpeningReset();

            // 调用 saveWithWorkingPapers 保存（与 WP_TO_WP 使用相同接口）
            await saveWithWorkingPapers(orchestrateHeader);

            setSnack({open: true, message: 'FS Opening Adjust Successful!', severity: 'success'});

            // 使用 React Query 刷新数据
            queryClient.invalidateQueries({
                queryKey: ['adjustment', 'list', companyIdState, annualIdState]
            });

            // 清理状态
            adjustmentStore.clearAll();
            adjustmentStore.setHeader(null);
            adjustmentStore.setCurrentMode(null);
            // 清空 mode2 辅助输入状态
            adjustmentStore.clearMode2Assist();
            setHasUpdated(false);
            setFsOpeningEditMode(false);
            // 清空错误状态
            setFsOpeningError(null);
            setAllExclusiveCodesZero(true);

        } catch (e: any) {
            setSnack({open: true, message: e?.message || '保存失败。', severity: 'error'});
        } finally {
            setSaving(false);
        }
    };

    // 3.2 Commit 逻辑（用于所有调整完成后进入下一步流程）
    const handleCommit = async () => {
        if (!companyIdState || !annualIdState) return;

        const activeId = adjustmentStore.activeHeaderId;
        if (!activeId) {
            setSnack({open: true, message: '当前不存在可提交的调整。', severity: 'warning'});
            return;
        }

        try {
            setSaving(true);

            // 调用后端 commit 接口（传入 true 表示 commit）
            await postAdjustment(activeId, true);

            setSnack({open: true, message: 'Commit Successful!', severity: 'success'});

            // 使用 React Query 刷新数据
            queryClient.invalidateQueries({
                queryKey: ['adjustment', 'list', companyIdState, annualIdState]
            });

            // 清理状态
            adjustmentStore.clearAll();
            adjustmentStore.setHeader(null);
            adjustmentStore.setCurrentMode(null);
            // 清空 mode2 辅助输入状态
            adjustmentStore.clearMode2Assist();
            setHasUpdated(false);
            setFsOpeningEditMode(false);

        } catch (e: any) {
            setSnack({open: true, message: e?.message || 'Commit 失败。', severity: 'error'});
        } finally {
            setSaving(false);
        }
    };

    // 3.3 取消当前调整模式
    const handleCancel = () => {
        // 触发清空 BalanceSheet 和 ProfitLoss 中的 fsOpeningAmounts
        adjustmentStore.triggerFsOpeningReset();
        // 清理状态
        adjustmentStore.setHeader(null);
        adjustmentStore.setCurrentMode(null);
        // 清空 mode2 辅助输入状态
        adjustmentStore.clearMode2Assist();
        setHasUpdated(false);
        setFsOpeningEditMode(false);
        // 清空错误状态
        setFsOpeningError(null);
        setAllExclusiveCodesZero(true);

        setSnack({open: true, message: 'The current adjustment mode has been cancelled.', severity: 'info'});
    };

    // ✅ 初始加载或重新加载时显示 Skeleton
    if (error && !adjustmentData) return <Alert severity="error">{error}</Alert>;

    const statusOpen = Boolean(statusAnchorEl);

    const pageButtonHandle = (
        <Box>
            {/* FS_TO_FS 模式的启动按钮 */}
            {/* {!showSkeleton && currentMode !== AdjustmentMode.ADJUST_FS_TO_FS && (
                <Button
                    size="small"
                    variant="outlined"
                    onClick={startFsToFsMode}
                    sx={{mr: 1}}
                >
                    Adjustment
                </Button>
            )} */}

            {showSkeleton && (
                <>
                    <Skeleton variant="rounded" width={110} height={32} sx={{display: 'inline-block', mr: 1}}/>
                    <Skeleton variant="rounded" width={80} height={32} sx={{display: 'inline-block'}}/>
                </>
            )}

            {/* 模式3（FS_OPENING）显示 Post 和 Cancel 按钮 */}
            {!showSkeleton && currentMode === AdjustmentMode.ADJUST_FS_TO_FS && (
                <>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={saving || !hasUpdated || !!fsOpeningError || allExclusiveCodesZero}
                        onClick={handleAdjustment}
                        sx={{mr: 1}}
                        title={fsOpeningError || (allExclusiveCodesZero ? 'Please enter at least one adjustment value.' : undefined)}
                    >
                        Post
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={saving}
                        onClick={handleCancel}
                        sx={{mr: 1}}
                    >
                        Cancel
                    </Button>
                </>
            )}

            {/* 只在非 adjustMode 状态下显示 Commit 按钮 */}
            {!showSkeleton && currentMode === null && (
                <>
                    <Button
                        size="small"
                        variant={previewDialogOpen ? 'contained' : 'outlined'}
                        color="info"
                        startIcon={
                            openingPreview
                                ? <CircularProgress size={14} color="inherit"/>
                                : <VisibilityOutlinedIcon fontSize="small"/>
                        }
                        disabled={openingPreview || exportingIssues}
                        onClick={() => handlePreviewClick()}
                        sx={{mr: 1}}
                    >
                        Preview
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={
                            exportingIssues
                                ? <CircularProgress size={14} color="inherit"/>
                                : <DownloadIcon fontSize="small"/>
                        }
                        disabled={exportingIssues || openingPreview}
                        onClick={() => void handleExportIssuesOutstanding()}
                        sx={{mr: 1}}
                    >
                        OUTSTANDING TO CLIENT
                    </Button>
                    <Button
                        size="small"
                        variant={statusOpen ? 'contained' : 'outlined'}
                        color="secondary"
                        startIcon={saving ? <CircularProgress size={14} color="inherit"/> : <CompareArrowsIcon fontSize="small"/>}
                        disabled={saving}
                        onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                        sx={{mr: 1}}
                    >
                        Status
                    </Button>
                    <Popover
                        open={statusOpen}
                        anchorEl={statusAnchorEl}
                        onClose={() => setStatusAnchorEl(null)}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                        transformOrigin={{vertical: 'top', horizontal: 'center'}}
                    >
                        <Stack direction="row" spacing={1} sx={{p: 1.5}}>
                            <Button
                                size="small"
                                color="warning"
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={14}/> : undefined}
                                disabled={saving}
                                onClick={() => {
                                    setStatusAnchorEl(null);
                                    void handleModify();
                                }}
                            >
                                Modify
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={14}/> : undefined}
                                disabled={saving}
                                onClick={() => {
                                    setStatusAnchorEl(null);
                                    void handleCommit();
                                }}
                            >
                                Commit
                            </Button>
                        </Stack>
                    </Popover>
                </>
            )}
        </Box>
    )

    return (
        <WpSnackbarContext.Provider value={handleWpSnackbar}>
        <Box sx={{
            p: '20px', width: '100%', height: '100%',
            maxHeight: '100%', overflow: 'hidden',
            boxSizing: 'border-box', display: 'flex', flexDirection: 'column'
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {companyName} - {adjustmentData?.yearOfAssessment || yearOfAssessment} Adjustment
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    {!showSkeleton && adjustmentFetching && (
                        <CircularProgress size={18}/>
                    )}
                    <Button size="small" disabled={saving} onClick={() => { void handleBack(); }}>Back</Button>
                </Stack>
            </Stack>
            <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                <SplitTabsPanel
                    renderAll
                    contentWrapperSx={{height: '100%', overflow: 'hidden', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                    selectedKey={fsTabKey}
                    onChange={setFsTabKey}
                    // P6-C：MULTI·PL 时 Period Tabs 进 headerLeft，与 Export / maximize 同一 sticky 行
                    // TotalSingle：Tabs 左侧 MultipleIcon/TotalIcon 切换；total 时隐藏 Tabs
                    // Icon/auto-compact：左侧栏可缩；Period Tabs 始终保留（避免选中态随 compact 消失）
                    headerLeft={
                        isPlMulti && fsTabKey === 'PL' ? (
                            <FsPlPeriodHeaderLeft
                                plMultiViewMode={plMultiViewMode}
                                onToggleMultiViewMode={() => setPlMultiViewMode((m) => (m === 'multiple' ? 'total' : 'multiple'))}
                                plPeriodKey={plPeriodKey}
                                onPeriodChange={setPlPeriodKey}
                                plSlices={plSlices}
                            />
                        ) : undefined
                    }
                    headerRight={pageButtonHandle}
                    height="100%"
                    errorMsg={fsOpeningError}
                    warningMsg={fsOpeningWarning}
                    tabs={[
                        {
                            key: 'PROGRAM',
                            label: 'Program',
                            icon: <MenuBookOutlinedIcon sx={{fontSize: 24}}/>,
                            content: (
                                <AuditProgramPanel
                                    companyId={companyIdState}
                                    annualId={annualIdState}
                                    companyName={companyName}
                                />
                            ),
                        },
                        {
                            key: 'BS',
                            label: 'Balance Sheet',
                            icon: <BalanceSheetOutlinedIcon sx={{fontSize: 24}}/>,
                            content: (
                                <>
                                    {showSkeleton ? (
                                        tabContentSkeleton
                                    ) : (
                                        <BalanceSheet
                                            mode="adjustment"
                                            annualId={annualIdState}
                                            onScheduleClick={(item: any) => scheduleClick(item, 'BS')}
                                            fsOpeningEditMode={fsOpeningEditMode}
                                            onFsOpeningDataChange={handleFsOpeningDataChange}
                                            hideLastYearColumns={firstYearAudit}
                                            onAdjustmentCellClick={handleAdjustmentCellClick}
                                            adjustTotalMode={adjustTotalMode}
                                            onAdjustTotalModeChange={handleAdjustTotalModeChange}
                                            onAdjustColumnCountChange={handleBsAdjustColumnCountChange}
                                            auditedRounded={auditedRounded}
                                            onAuditedRoundedChange={handleAuditedRoundedChange}
                                            onRollForwardWarningChange={setFsOpeningWarning}
                                            itemAliasEditEnabled={itemAliasEditEnabled}
                                            onItemAliasChange={handleItemAliasChange}
                                        />
                                    )}
                                </>
                            )
                        },
                        {
                            key: 'PL',
                            label: 'Income Statement',
                            icon: <ProfitLossOutlinedIcon sx={{fontSize: 24}}/>,
                            content: (
                                <>
                                    {showSkeleton ? (
                                        tabContentSkeleton
                                    ) : isPlMulti ? (
                                        <Box sx={{height: '100%', minHeight: 0, overflow: 'hidden'}}>
                                            {/* Keep period instances mounted (display:none) for Audited chain; Total overlays when active */}
                                            {plSlices.map((slice) => (
                                                <Box
                                                    key={slice.key}
                                                    sx={{
                                                        display: plMultiViewMode === 'multiple'
                                                            && slice.key === (plPeriodKey || plSlices[0]?.key)
                                                            ? 'block'
                                                            : 'none',
                                                        height: '100%',
                                                        minHeight: 0,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <ProfitLoss
                                                        mode="adjustment"
                                                        annualId={annualIdState}
                                                        periodScope={{
                                                            periodFrom: slice.periodFrom,
                                                            periodTo: slice.periodTo,
                                                            yearOfAssessment: slice.yearOfAssessment,
                                                            sliceKey: slice.key,
                                                        }}
                                                        plMultiViewMode="multiple"
                                                        onScheduleClick={(item: any) => scheduleClick(item, 'PL', slice)}
                                                        fsOpeningEditMode={fsOpeningEditMode}
                                                        onFsOpeningDataChange={handleFsOpeningDataChange}
                                                        // MULTI single-tab: hide Last Year only on first period when firstAudit=1
                                                        hideLastYearColumns={firstYearAudit && slice.key === plSlices[0]?.key}
                                                        onAdjustmentCellClick={handleAdjustmentCellClick}
                                                        adjustTotalMode={adjustTotalMode}
                                                        onAdjustTotalModeChange={handleAdjustTotalModeChange}
                                                        onAdjustColumnCountChange={handlePlAdjustColumnCountChange}
                                                        auditedRounded={auditedRounded}
                                                        onAuditedRoundedChange={handleAuditedRoundedChange}
                                                        itemAliasEditEnabled={itemAliasEditEnabled}
                                                        onItemAliasChange={handleItemAliasChange}
                                                    />
                                                </Box>
                                            ))}
                                            {plMultiViewMode === 'total' ? (
                                                <Box sx={{height: '100%', minHeight: 0, overflow: 'hidden'}}>
                                                    <ProfitLoss
                                                        mode="adjustment"
                                                        annualId={annualIdState}
                                                        plMultiViewMode="total"
                                                        onScheduleClick={(item: any) => scheduleClick(item, 'PL')}
                                                        fsOpeningEditMode={false}
                                                        onFsOpeningDataChange={handleFsOpeningDataChange}
                                                        hideLastYearColumns={firstYearAudit}
                                                        onAdjustmentCellClick={handleAdjustmentCellClick}
                                                        adjustTotalMode={adjustTotalMode}
                                                        onAdjustTotalModeChange={handleAdjustTotalModeChange}
                                                        onAdjustColumnCountChange={handlePlAdjustColumnCountChange}
                                                        auditedRounded={auditedRounded}
                                                        onAuditedRoundedChange={handleAuditedRoundedChange}
                                                        readOnly
                                                    />
                                                </Box>
                                            ) : null}
                                        </Box>
                                    ) : (
                                        <ProfitLoss
                                            mode="adjustment"
                                            annualId={annualIdState}
                                            onScheduleClick={(item: any) => scheduleClick(item, 'PL')}
                                            fsOpeningEditMode={fsOpeningEditMode}
                                            onFsOpeningDataChange={handleFsOpeningDataChange}
                                            hideLastYearColumns={firstYearAudit}
                                            onAdjustmentCellClick={handleAdjustmentCellClick}
                                            adjustTotalMode={adjustTotalMode}
                                            onAdjustTotalModeChange={handleAdjustTotalModeChange}
                                            onAdjustColumnCountChange={handlePlAdjustColumnCountChange}
                                            auditedRounded={auditedRounded}
                                            onAuditedRoundedChange={handleAuditedRoundedChange}
                                            itemAliasEditEnabled={itemAliasEditEnabled}
                                            onItemAliasChange={handleItemAliasChange}
                                        />
                                    )}
                                </>
                            )
                        },
                        {
                            key: 'CF',
                            label: 'Cashflow',
                            icon: <CashFlowOutlinedIcon sx={{fontSize: 24}}/>,
                            content: (
                                <>
                                    {showSkeleton ? (
                                        tabContentSkeleton
                                    ) : (
                                        <Cashflow
                                            companyId={companyIdState}
                                            annualId={annualIdState}
                                            active={fsTabKey === 'CF'}
                                            onOpenFsCode={(code) => openCodeRef.current(code)}
                                        />
                                    )}
                                </>
                            ),
                        },
                        {
                            key: 'ISSUES',
                            label: 'MARK AS ISSUE',
                            icon: <ReportProblemOutlinedIcon sx={{fontSize: 24}}/>,
                            content: (
                                <IssuesOutstandingPanel
                                    companyId={companyIdState}
                                    annualId={annualIdState}
                                    active={fsTabKey === 'ISSUES'}
                                    onOpenSchedule={handleOpenIssueSchedule}
                                />
                            ),
                        },
                        // {key: 'AUDIT_PROGRAM', label: 'Audit Program', content: (<><C7MaterialitySummary/></>)}
                    ]}
                />
                {/* Dynamic dialog launcher for schedule clicks */}
                <AdjustDialogLauncher
                    code={dialogCode ?? undefined}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    payload={dialogPayload}
                    onSnackbar={handleWpSnackbar}
                />
                {/* AdjustTabsDialog - now at FsAdjustment level */}
                <AdjustTabsDialog/>

                {/* 特殊组件渲染：BankLoan */}
                <BankLoan
                    open={specialDialogType === 'BankLoan'}
                    onClose={() => {
                        setSpecialDialogType(null);
                        setSpecialDialogPayload(null);
                    }}
                    payload={specialDialogPayload}
                    onSnackbar={handleSpecialWpSnackbar}
                />

                {/* 特殊组件渲染：VehicleLoan */}
                <VehicleLoan
                    open={specialDialogType === 'VehicleLoan'}
                    onClose={() => {
                        setSpecialDialogType(null);
                        setSpecialDialogPayload(null);
                    }}
                    payload={specialDialogPayload}
                    onSnackbar={handleSpecialWpSnackbar}
                />

                <FsPreviewDownloadDialog
                    open={previewDialogOpen}
                    onClose={() => setPreviewDialogOpen(false)}
                    companyId={companyIdState}
                    annualId={annualIdState}
                    periods={isPlMulti ? plSlices : undefined}
                    initialYearOfAssessment={
                        isPlMulti
                            ? (activePlSlice?.yearOfAssessment || plSlices[0]?.yearOfAssessment || null)
                            : null
                    }
                    onSnack={(message, severity) => setSnack({open: true, message, severity})}
                />
            </Box>

            {/* WP save snackbar: only visible after paper-registry / special WP dialog has closed */}
            <SnackbarField
                open={!!snack?.open && !dialogOpen && !specialDialogType}
                message={snack?.message ?? ''}
                severity={snack?.severity ?? 'info'}
                autoHideDuration={3000}
                onClose={() => setSnack(s => (s ? {...s, open: false} : s))}
            />

            {/* FS_TO_FS (Mode 2) Saving Progress Overlay */}
            <Backdrop
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                open={saving && currentMode === AdjustmentMode.ADJUST_FS_TO_FS}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>

            {/* Loading Overlay for adjustment cell click */}
            <Backdrop
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                open={adjustmentCellLoading}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
        </WpSnackbarContext.Provider>
    );
}
